import React from 'react';
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import LoanAmountStep from "./LoanAmountStep.jsx";
import LoanPurposeStep from "./LoanPurposeStep.jsx";
import BusinessInfoStep from "./BusinessInfoStep.jsx";
import BusinessAddressStep from "./BusinessAddressStep.jsx";
import LoanTimingStep from "./LoanTimingStep.jsx";
import CollateralStep from "./CollateralStep.jsx";
import ContactInfoStep from "./ContactInfoStep.jsx";
import RevenueStep from "./RevenueStep.jsx";
import ProfitStep from "./ProfitStep.jsx";
import ResourcesStep from "./ResourcesStep.jsx";
import StepResults from './StepResults.jsx';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { saveFormData, getFormData, hasCookieConsent } from "@/utils/cookieUtils";
import { getApiUrl } from '../../config/api.config';
import { colors, semanticColors } from '@/constants/colors.jsx';
import { trackFormStep, trackFormCompletion } from '../../utils/trackEvent.jsx';

const steps = [
  {
    number: 1,
    title: "Complete a short questionnaire",
    description: "Provide information about you and your business.",
  },
  {
    number: 2,
    title: "Get matched with trusted riba-free solutions",
    description: "You'll be matched with either a riba-free loan provider or services to help with your loan application.",
  },
  {
    number: 3,
    title: "Next steps",
    description: "The trusted partner you are matched with will reach out to you to determine next steps.",
  },
];

// Step names for tracking
const stepNames = [
  'how_it_works',
  'loan_amount',
  'loan_purpose',
  'loan_timing',
  'collateral',
  'business_info',
  'business_address',
  'revenue',
  'profit',
  'contact_info',
  'resources',
  'results'
];

const MultiStepForm = () => {
  // Always start at step 0 but keep saved data
  const savedData = getFormData();
  
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false); // Prevent multiple submissions
  const [formData, setFormData] = useState({
    loanAmount: savedData?.loanAmount || "",
    loanPurpose: savedData?.loanPurpose || "",
    loanTiming: savedData?.loanTiming || "",
    collateral: savedData?.collateral || [],
    businessInfo: savedData?.businessInfo || {
      businessName: "",
      businessType: "",
      businessIndustry: "",
      timeInBusiness: ""
    },
    businessAddress: savedData?.businessAddress || {
      streetAddress: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: ""
    },
    revenue: savedData?.revenue || "",
    profit: savedData?.profit || "",
    contactInfo: savedData?.contactInfo || {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      preferredContact: "",
      preferredTime: ""
    },
    resources: savedData?.resources || []
  });

  // Load saved form data on mount and when cookie consent changes
  useEffect(() => {
    const loadSavedData = () => {
      const data = getFormData();
      if (data) {
        setFormData(prevData => ({
          ...prevData,
          ...data
        }));
      }
    };

    // Load data initially
    loadSavedData();

    // Set up an interval to check for cookie consent changes
    const intervalId = setInterval(() => {
      if (hasCookieConsent()) {
        loadSavedData();
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const scrollToTop = () => {
    const formElement = document.getElementById('loan-form');
    if (formElement) {
      const formPosition = formElement.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: formPosition - 20, // Subtracting 20px for some padding from the top
        behavior: 'smooth'
      });
    }
  };

  const submitFormData = async (data) => {
    // Prevent multiple submissions
    if (hasSubmitted || isSubmitting) {
      console.log('Form submission already in progress or completed');
      return true;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setHasSubmitted(true);

      console.log('Submitting halal loan matching form...');

      const response = await fetch(`${getApiUrl()}/loan-application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        // Try to get error message from response
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Halal loan matching form submitted successfully:', result);
      
      // Clear saved form data from cookies after successful submission
      saveFormData(null);
      
      return true;
    } catch (error) {
      console.error('Error submitting halal loan matching form:', error);
      setSubmitError(error.message);
      setHasSubmitted(false); // Allow retry on error
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNextStep = async (data) => {
    let updatedFormData = { ...formData };
    
    // Track form step progression
    trackFormStep(step, stepNames[step], formData);
    
    switch (step) {
      case 0:
        setStep(1);
        break;
      case 1:
        updatedFormData = { ...formData, loanAmount: data };
        break;
      case 2:
        updatedFormData = { ...formData, loanPurpose: data };
        break;
      case 3:
        updatedFormData = { ...formData, loanTiming: data };
        break;
      case 4:
        updatedFormData = { ...formData, collateral: data };
        break;
      case 5:
        updatedFormData = { ...formData, businessInfo: data };
        break;
      case 6:
        updatedFormData = { ...formData, businessAddress: data };
        break;
      case 7:
        updatedFormData = { ...formData, revenue: data };
        break;
      case 8:
        updatedFormData = { ...formData, profit: data };
        break;
      case 9:
        updatedFormData = { ...formData, contactInfo: data };
        break;
      case 10:
        updatedFormData = { ...formData, resources: data };
        
        // Submit form data on final step
        const success = await submitFormData(updatedFormData);
        if (!success) {
          // Don't proceed to next step if submission failed
          return;
        }
        
        // Track form completion
        trackFormCompletion(updatedFormData);
        break;
      default:
        break;
    }

    // Save form data after each step
    if (step > 0) {
      setFormData(updatedFormData);
      saveFormData(updatedFormData);
      setStep(step + 1);
    }
    
    scrollToTop();
  };

  const renderHowItWorks = () => (
    <div className="flex flex-col">
      <div className="text-center space-y-1 mb-3">
      <motion.h1 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-3xl md:text-3xl font-bold"
          style={{ color: semanticColors.button.primary.background }}
        >
          Find Halal Funding
        </motion.h1>
        <motion.h3 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-xl md:text-3xl font-bold text-gray-900"
        >
          How it Works
        </motion.h3>
        <motion.div 
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="w-12 h-0.5 mx-auto"
          style={{ backgroundColor: semanticColors.button.primary.background }}
        />
      </div>

      <div className="grid gap-2 md:gap-3 mb-4 max-w-[95%] mx-auto w-full">
        {steps.map((step, index) => (
          <motion.div
            key={step.number}
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{
              duration: 0.5,
              delay: index * 0.2,
              ease: "easeOut"
            }}
            className="bg-transparent md:bg-white rounded-lg p-2 md:p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-2 md:gap-3">
              <div className="flex-shrink-0">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 260,
                    damping: 20,
                    delay: index * 0.2 + 0.2
                  }}
                  whileHover={{ scale: 1.1 }}
                  className="w-7 h-7 md:w-8 md:h-8 rounded-full text-white flex items-center justify-center text-base font-semibold shadow-sm"
                  style={{ backgroundColor: semanticColors.button.primary.background }}
                >
                  {step.number}
                </motion.div>
              </div>
              <motion.div
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.2 + 0.3,
                  ease: "easeOut"
                }}
                className="space-y-0.5 md:space-y-1"
              >
                <h3 className="text-base md:text-lg font-semibold text-gray-900">{step.title}</h3>
                <p className="text-sm md:text-base text-gray-600 leading-snug">{step.description}</p>
              </motion.div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-auto px-0 md:px-2">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.9 }}
        >
          <Button
            onClick={() => handleNextStep()}
            className="w-full text-white py-3 text-base font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
            style={{ 
              backgroundColor: semanticColors.button.primary.background,
              '&:hover': {
                backgroundColor: semanticColors.button.primary.hover
              }
            }}
          >
            Continue
          </Button>
        </motion.div>
      </div>
    </div>
  );

  const renderStep = () => {
    // Show loading or error state in the final step
    if (step === 11) {
      return (
        <StepResults 
          formData={formData}
          isSubmitting={isSubmitting}
          error={submitError}
          onRetry={() => submitFormData(formData)}
        />
      );
    }

    switch (step) {
      case 0:
        return renderHowItWorks();
      case 1:
        return <LoanAmountStep 
          onNext={handleNextStep}
          initialValue={formData.loanAmount}
        />;
      case 2:
        return <LoanPurposeStep 
          onNext={handleNextStep}
          initialValue={formData.loanPurpose}
        />;
      case 3:
        return <LoanTimingStep 
          onNext={handleNextStep}
          initialValue={formData.loanTiming}
        />;
      case 4:
        return <CollateralStep 
          onNext={handleNextStep}
          initialValue={formData.collateral}
        />;
      case 5:
        return <BusinessInfoStep 
          onNext={handleNextStep}
          initialValue={formData.businessInfo}
        />;
      case 6:
        return <BusinessAddressStep 
          onNext={handleNextStep}
          initialValue={formData.businessAddress}
        />;
      case 7:
        return <RevenueStep 
          onNext={handleNextStep}
          initialValue={formData.revenue}
        />;
      case 8:
        return <ProfitStep 
          onNext={handleNextStep}
          initialValue={formData.profit}
        />;
      case 9:
        return <ContactInfoStep 
          onNext={handleNextStep}
          initialValue={formData.contactInfo}
        />;
      case 10:
        return <ResourcesStep 
          onNext={handleNextStep}
          initialValue={formData.resources}
          isSubmitting={isSubmitting}
        />;
      default:
        return <StepResults formData={formData}/>;
    }
  };

  return (
    <div className="bg-[#F4F7FF] flex items-center justify-center p-1 md:p-2">
      <Card className="w-full max-w-2xl bg-white shadow-lg">
        <CardContent className="p-2 md:p-3">
          {renderStep()}
        </CardContent>
      </Card>
    </div>
  );
};

export default MultiStepForm;